import React, { Suspense, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { Router, Route, Switch } from 'wouter';
import { AuthProvider } from './hooks/use-auth';
import { ToastProvider } from './hooks/use-toast';
import { YearContextProvider } from './contexts/YearContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ProtectedRoute } from './lib/protected-route';
import { Toaster } from './components/ui/toaster';

// Pages
import AuthPage from './pages/auth-page';
import JoinPage from './pages/join-page';
import Dashboard from './pages/dashboard';
import AnalyticsDashboard from './pages/analytics-dashboard';
import CompaniesDashboard from './pages/companies-dashboard';
import CompanyProfile from './pages/company-profile';
import ScopeEntry from './pages/scope-entry';
import EcoWizardPage from './pages/ghg-wizard';
import ResultsPage from './pages/results-page';
import Reports from './pages/reports';
import Settings from './pages/settings';
import PricingPage from './pages/pricing-page';
const CheckoutPage = React.lazy(() => import('./pages/checkout-page'));
import SubscriptionSuccess from './pages/subscription-success';
import SubscriptionCanceled from './pages/subscription-canceled';
import NotFound from './pages/not-found';

import './index.css';

function AppRoutes() {
  return (
    <Router>
      <Switch>
        <Route path="/auth" component={AuthPage} />
        <Route path="/join" component={JoinPage} />
        <Route path="/pricing" component={PricingPage} />
        <Route path="/subscription/success" component={SubscriptionSuccess} />
        <Route path="/subscription/canceled" component={SubscriptionCanceled} />
        <ProtectedRoute
          path="/checkout/:planId"
          component={() => (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
              <CheckoutPage />
            </Suspense>
          )}
        />
        <ProtectedRoute path="/" component={Dashboard} />
        <ProtectedRoute path="/analytics" component={AnalyticsDashboard} />
        <ProtectedRoute path="/companies" component={CompaniesDashboard} />
        <ProtectedRoute path="/company-profile" component={CompanyProfile} />
        <ProtectedRoute path="/company-profile/:id" component={CompanyProfile} />
        <ProtectedRoute path="/scope/:scopeType" component={ScopeEntry} />
        <ProtectedRoute path="/ghg-wizard" component={EcoWizardPage} />
        <ProtectedRoute path="/results" component={ResultsPage} />
        <ProtectedRoute path="/reports" component={Reports} />
        <ProtectedRoute path="/settings" component={Settings} />
        <Route component={NotFound} />
      </Switch>
    </Router>
  );
}

function App() {
  // Initialize feather icons globally
  useEffect(() => {
    const initializeIcons = () => {
      if (window.feather) {
        window.feather.replace();
      }
    };
    
    // Initialize immediately
    initializeIcons();
    
    // Also initialize after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(initializeIcons, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <YearContextProvider>
              <div className="min-h-screen bg-app-theme">
                <AppRoutes />
                <Toaster />
              </div>
            </YearContextProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;